import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import { useEffect } from 'react';
import baseFetcher, { FetcherError } from 'shared/helpers/baseFetcher';

export interface UserMember {
  job_title: string;
  site: string;
  tour_completed_at: string | null;
  onboarding_completed_at: string | null;
  handover_preferences?: string[];
}

export interface UserOrganization {
  id: string;
  name: string;
}

export interface UserScope {
  full_scope: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  nickname?: string;
  language: string;
  official_language?: string;
  is_demo: boolean;
  is_internal?: boolean;
  is_team_lead?: boolean;
  has_shift_notes?: boolean;
  member: UserMember;
  organization: UserOrganization;
  scopes: UserScope[];
}

interface IdentityInput {
  nickname: string;
  pronouns: string[];
}

interface LanguageInput {
  language: string;
}

interface CommunicationPreferencesInput {
  communication_preference: string;
  feedback_mode: string;
  feedback_timing: string;
}

export function useUser() {
  const query = useQuery<User>({
    queryKey: [APIKey.USER],
    queryFn: () => baseFetcher(apiRoutes.userMe()),
    enabled: !!localStorage.getItem('authorization'),
  });

  useEffect(() => {
    if (query.error instanceof FetcherError && query.error.status === 401) {
      localStorage.removeItem('authorization');
    }
  }, [query.error]);

  return query;
}

export function useUpdateIdentityMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IdentityInput) =>
      baseFetcher(apiRoutes.userIdentity(), { method: 'PUT', data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.USER] });
    },
  });
}

export function useUpdateLanguageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LanguageInput) =>
      baseFetcher(apiRoutes.userLanguage(), { method: 'PUT', data }),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });
}

export function useUpdateTourCompletedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => baseFetcher(apiRoutes.tourCompleted(), { method: 'PUT' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.USER] });
    },
  });
}

export function useUpdateCommunicationPreferencesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CommunicationPreferencesInput) =>
      baseFetcher(apiRoutes.communicationPreferences(), {
        method: 'PUT',
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.USER] });
    },
  });
}
